import { defineMenu, MENU_CHOOSE_EVENT, MENU_CLOSE_EVENT, type MenuElement, type MenuView } from 'BomdisoftComponents/menu/element';
import { delegateTooltips } from 'BomdisoftComponents/tooltip/delegate';
import { renderTooltip } from 'BomdisoftComponents/tooltip/render';

export type UserAvatarMenuBehaviour = {
  onItem?: (index: number) => void;
  onSignOut?: () => void;
  testId?: string;
  tooltipId: string;
};

export const delegateUserAvatarMenu = (root: HTMLElement, { onItem, onSignOut, testId = 'user-avatar-menu', tooltipId }: UserAvatarMenuBehaviour) => {
  const view = root.ownerDocument.defaultView as MenuView | null;
  const trigger = root.querySelector<HTMLButtonElement>('.bomdisoft-user-avatar-menu-trigger');
  const menuId = trigger?.getAttribute('aria-controls') ?? null;
  const menu = menuId === null ? null : (root.ownerDocument.getElementById(menuId) as MenuElement | null);
  if (view === null || trigger === null || menu === null) return () => {};
  defineMenu(view);
  const tooltipRoot = root.getRootNode();
  if (tooltipRoot.nodeType === tooltipRoot.DOCUMENT_NODE || tooltipRoot.nodeType === tooltipRoot.DOCUMENT_FRAGMENT_NODE) delegateTooltips(tooltipRoot as Document | ShadowRoot);
  menu.anchorRef = { current: trigger };
  const name = trigger.querySelector<HTMLElement>('.bomdisoft-user-avatar-menu-trigger-name');

  const setOpen = (open: boolean) => {
    trigger.setAttribute('aria-expanded', String(open));
    menu.open = open;
  };

  const handleTrigger = () => setOpen(!menu.open);
  const handleClosed = () => setOpen(false);

  const handleMenuClick = (event: Event) => {
    const target = event.target instanceof view.Element ? event.target : null;
    const row = target?.closest('.bomdisoft-menu-item') ?? null;
    if (row === null) return;
    if (row.getAttribute('data-testid') === `${testId}-sign-out`) {
      onSignOut?.();
      return;
    }
    const rows = [...menu.querySelectorAll('.bomdisoft-menu-item')].filter(item => item.getAttribute('data-testid') !== `${testId}-sign-out`);
    const index = rows.indexOf(row);
    if (index >= 0) onItem?.(index);
  };

  const wrapped = () => (trigger.parentElement?.classList.contains('bomdisoft-tooltip-trigger') ? trigger.parentElement : null);

  const measure = () => {
    if (name === null) return;
    const truncated = name.scrollWidth > name.clientWidth;
    const wrapper = wrapped();
    if (truncated && wrapper === null) {
      const template = root.ownerDocument.createElement('template');
      template.innerHTML = renderTooltip({ children: '', id: tooltipId, placement: 'top', title: name.textContent ?? '' }).value;
      const [anchor, bubble] = [...template.content.children];
      if (anchor === undefined || bubble === undefined) return;
      trigger.replaceWith(anchor, bubble);
      anchor.append(trigger);
    }
    if (!truncated && wrapper !== null) {
      wrapper.nextElementSibling?.remove();
      wrapper.replaceWith(trigger);
    }
  };

  const observer = name !== null && typeof view.ResizeObserver === 'function' ? new view.ResizeObserver(measure) : null;
  if (name !== null) {
    measure();
    observer?.observe(name);
  }

  trigger.addEventListener('click', handleTrigger);
  menu.addEventListener(MENU_CLOSE_EVENT, handleClosed);
  menu.addEventListener(MENU_CHOOSE_EVENT, handleClosed);
  menu.addEventListener('click', handleMenuClick);
  return () => {
    observer?.disconnect();
    trigger.removeEventListener('click', handleTrigger);
    menu.removeEventListener(MENU_CLOSE_EVENT, handleClosed);
    menu.removeEventListener(MENU_CHOOSE_EVENT, handleClosed);
    menu.removeEventListener('click', handleMenuClick);
  };
};
